"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import Button from "./Button";
import type { CardProps } from "@/types/components";

// Helper function to strip HTML tags and get plain text
function stripHtmlTags(html: string | undefined): string {
  if (!html) return "";
  if (typeof window === "undefined") {
    // Server-side: simple regex replacement
    return html.replace(/<[^>]*>/g, "");
  }
  // Client-side: use DOM
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || "";
}

export default function Card({
  name,
  description,
  image,
  qtd,
  href,
  variant = "horizontal",
  className,
  sponsored,
}: CardProps) {
  const [imageLoading, setImageLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Set mounted to true after component mounts
    const timer = setTimeout(() => setMounted(true), 0);
    return () => clearTimeout(timer);
  }, []);

  if (variant === "vertical") {
    const content = (
      <div
        className={`bg-white rounded-2xl border border-gray-200 overflow-hidden transition-all hover:shadow-lg hover:-translate-y-1 ${
          className ?? ""
        }`}
      >
        <div className="relative aspect-video bg-gray-100">
          {sponsored && (
            <span className="absolute top-2 left-2 z-10 bg-yellow-400 text-yellow-900 text-xs font-semibold px-2 py-1 rounded-full shadow-sm">
              Patrocinado
            </span>
          )}
          {mounted && imageLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-mygreen/30 border-t-mygreen rounded-full animate-spin"></div>
            </div>
          )}
          <Image
            src={image || ""}
            alt={name}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            unoptimized
            className={`object-cover transition-opacity duration-300 ${
              mounted && imageLoading ? "opacity-0" : "opacity-100"
            }`}
            onLoad={() => setImageLoading(false)}
          />
        </div>
        <div className="p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">
            {name}
          </h3>
          {description && (
            <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
              {stripHtmlTags(description)}
            </p>
          )}
          {typeof qtd === "number" && (
            <p className="text-sm text-mygreen font-medium mt-3">
              {qtd} {qtd === 1 ? "local disponível" : "locais disponíveis"}
            </p>
          )}
        </div>
      </div>
    );

    if (href) {
      return (
        <Link href={href} className="block">
          {content}
        </Link>
      );
    }

    return content;
  }

  if (variant === "horizontal-mobile") {
    return (
      <Link
        href={href || "#"}
        className={`bg-white rounded-2xl border border-gray-100 overflow-hidden flex items-center gap-4 transition-all hover:shadow-md ${
          className ?? ""
        }`}
      >
        <div className="relative w-20 h-20 shrink-0 p-2">
          {mounted && imageLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-6 h-6 border-3 border-mygreen/30 border-t-mygreen rounded-full animate-spin"></div>
            </div>
          )}
          <Image
            src={image || ""}
            alt={name}
            width={112}
            height={112}
            unoptimized
            className={`w-full h-full object-cover transition-opacity duration-300 rounded-full ${
              mounted && imageLoading ? "opacity-0" : "opacity-100"
            }`}
            onLoad={() => setImageLoading(false)}
          />
        </div>
        <div className="flex-1 py-3 pr-4 flex flex-col justify-center">
          <div>
            {sponsored && (
              <span className="inline-block bg-yellow-400 text-yellow-900 text-xs font-semibold px-2 py-0.5 rounded-full mb-1">
                Patrocinado
              </span>
            )}
            <h3 className="font-semibold text-base mb-1 line-clamp-1">
              {name}
            </h3>
            <p className="text-gray-600 text-sm line-clamp-2 leading-snug">
              {stripHtmlTags(description)}
            </p>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "category") {
    return (
      <div
        className={`bg-white rounded-2xl border border-gray-200 overflow-hidden ${
          className ?? ""
        }`}
      >
        <div className="aspect-video relative bg-gray-100">
          {image ? (
            <>
              {mounted && imageLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-4 border-mygreen/30 border-t-mygreen rounded-full animate-spin"></div>
                </div>
              )}
              <Image
                src={image}
                alt={name}
                width={400}
                height={160}
                unoptimized
                className={`w-full h-full object-cover transition-opacity duration-300 ${
                  mounted && imageLoading ? "opacity-0" : "opacity-100"
                }`}
                onLoad={() => setImageLoading(false)}
              />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-6xl text-gray-300">📋</div>
            </div>
          )}
        </div>
        <div className="py-8 px-6 grid gap-2">
          <h3 className="text-xl font-semibold">{name}</h3>
          {typeof qtd === "number" && (
            <p className="text-sm text-gray-600">
              {qtd} {qtd === 1 ? "local" : "locais"}
            </p>
          )}
          <Button href={href}>Ver mais</Button>
        </div>
      </div>
    );
  }

  return (
    <Link
      href={href || "#"}
      className={`bg-white rounded-lg p-4 border border-gray-200 flex items-center gap-3 transition-all hover:shadow-lg hover:border-mygreen/30 relative ${
        className ?? ""
      }`}
    >
      {sponsored && (
        <span className="absolute top-2 right-2 z-10 bg-yellow-400 text-yellow-900 text-xs font-semibold px-2 py-1 rounded-full shadow-sm">
          Patrocinado
        </span>
      )}
      <div className="relative w-14 h-14 bg-gray-100 rounded-full shrink-0">
        {mounted && imageLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-6 h-6 border-3 border-mygreen/30 border-t-mygreen rounded-full animate-spin"></div>
          </div>
        )}
        <Image
          src={image || ""}
          alt={name}
          width={56}
          height={56}
          unoptimized
          className={`w-14 h-14 object-cover rounded-full transition-opacity duration-300 ${
            mounted && imageLoading ? "opacity-0" : "opacity-100"
          }`}
          onLoad={() => setImageLoading(false)}
        />
      </div>
      <div>
        <h3 className="text-lg font-semibold">{name}</h3>
        {description && (
          <p className="text-gray-600 text-sm">{stripHtmlTags(description)}</p>
        )}
      </div>
    </Link>
  );
}
